import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCart } from "@/context/CartContext";
import { Loader2, Check } from "lucide-react";
import { toast } from "sonner";

export default function Checkout({ store }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { items, total, clear } = useCart();
  const [f, setF] = useState({ nom: "", prenom: "", telephone: "", whatsapp: "", email: "", adresse: "", ville: "", livraison: "Standard", paiement: "Paiement à la livraison" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const acc = store.secondary_color || "#3DBB2C";
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return toast.error("Panier vide");
    setLoading(true);
    try {
      const { data } = await api.post(`/public/store/${slug}/orders`, {
        ...f, items: items.map((i) => ({ product_id: i.product_id, name: i.name, color: i.color, size: i.size, qty: i.qty, price: i.price })),
      });
      clear(); setDone(data.order);
    } catch (e2) { toast.error(apiError(e2)); }
    setLoading(false);
  };

  if (done) return (
    <div className="max-w-lg mx-auto px-5 py-20 text-center">
      <div className="h-16 w-16 rounded-full grid place-items-center mx-auto mb-4" style={{ background: acc }}><Check className="text-white" size={30} /></div>
      <h1 className="font-display text-4xl font-bold text-neutral-900 mb-2">Commande confirmée !</h1>
      <p className="text-neutral-600">Votre commande <b className="mono">{done.number}</b> a bien été enregistrée. La boutique vous contactera prochainement.</p>
      <Button onClick={() => navigate(`/store/${slug}`)} className="mt-8 text-white" style={{ background: acc }}>Retour à la boutique</Button>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-5 py-10 grid lg:grid-cols-[1fr_360px] gap-10">
      <form onSubmit={submit}>
        <h1 className="font-display text-4xl font-bold text-neutral-900 mb-6">Finaliser la commande</h1>
        <div className="grid grid-cols-2 gap-4">
          <div><Label className="text-sm text-neutral-700 mb-1.5 block">Prénom</Label><Input value={f.prenom} onChange={set("prenom")} required className="bg-white border-black/15" data-testid="checkout-prenom" /></div>
          <div><Label className="text-sm text-neutral-700 mb-1.5 block">Nom</Label><Input value={f.nom} onChange={set("nom")} required className="bg-white border-black/15" data-testid="checkout-nom" /></div>
          <div><Label className="text-sm text-neutral-700 mb-1.5 block">Téléphone</Label><Input value={f.telephone} onChange={set("telephone")} required className="bg-white border-black/15" data-testid="checkout-phone" /></div>
          <div><Label className="text-sm text-neutral-700 mb-1.5 block">WhatsApp</Label><Input value={f.whatsapp} onChange={set("whatsapp")} className="bg-white border-black/15" /></div>
          <div className="col-span-2"><Label className="text-sm text-neutral-700 mb-1.5 block">Email</Label><Input value={f.email} onChange={set("email")} type="email" className="bg-white border-black/15" /></div>
          <div className="col-span-2"><Label className="text-sm text-neutral-700 mb-1.5 block">Adresse</Label><Input value={f.adresse} onChange={set("adresse")} required className="bg-white border-black/15" data-testid="checkout-address" /></div>
          <div><Label className="text-sm text-neutral-700 mb-1.5 block">Ville</Label><Input value={f.ville} onChange={set("ville")} className="bg-white border-black/15" /></div>
          <div><Label className="text-sm text-neutral-700 mb-1.5 block">Livraison</Label>
            <Select value={f.livraison} onValueChange={(v) => setF({ ...f, livraison: v })}><SelectTrigger className="bg-white border-black/15"><SelectValue /></SelectTrigger>
              <SelectContent>{["Standard", "Express", "Retrait en boutique"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select></div>
          <div className="col-span-2"><Label className="text-sm text-neutral-700 mb-1.5 block">Mode de paiement</Label>
            <Select value={f.paiement} onValueChange={(v) => setF({ ...f, paiement: v })}><SelectTrigger className="bg-white border-black/15"><SelectValue /></SelectTrigger>
              <SelectContent>{["Paiement à la livraison", "Mobile Money", "Virement", "Carte bancaire"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select></div>
        </div>
        <Button type="submit" disabled={loading} className="mt-6 h-12 px-8 text-white font-semibold" style={{ background: acc }} data-testid="checkout-submit">
          {loading && <Loader2 className="animate-spin mr-2" size={16} />} Valider la commande
        </Button>
      </form>

      <div className="bg-white rounded-xl border border-black/10 p-5 h-fit">
        <h2 className="font-display text-xl font-bold text-neutral-900 mb-4">Récapitulatif</h2>
        <div className="space-y-3">
          {items.map((i, idx) => (
            <div key={idx} className="flex justify-between text-sm"><span className="text-neutral-700">{i.qty}× {i.name} <span className="text-neutral-400">({i.color}/{i.size})</span></span><span className="mono">{fmtMoney(i.price * i.qty)}</span></div>
          ))}
          {items.length === 0 && <p className="text-neutral-500 text-sm">Panier vide</p>}
        </div>
        <div className="border-t border-black/10 mt-4 pt-4 flex justify-between text-lg font-bold text-neutral-900"><span>Total</span><span className="mono">{fmtMoney(total)}</span></div>
      </div>
    </div>
  );
}
